import React, {memo, useEffect, useState} from "react";
import {keyframes} from "@emotion/react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import ButtonBase from "@mui/material/ButtonBase";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import {alpha} from "@mui/material/styles";
import {ArrowDownward, Downloading, Print} from "@mui/icons-material";
import {countWaitingTasks, getMachinePrintState, PRINT_STATE} from "../../Utils/machinePrintState";
import {getPreloadedTaskIds, isRequestPickedUp} from "../../Utils/printRequest";
import {printFirstTask} from "../../Service/TaskService";

const PICKUP_TIMEOUT_MS = 15000;

const STATE_DISPLAY = {
    [PRINT_STATE.OFF]: {label: "Éteinte", color: "error"},
    [PRINT_STATE.PRINTING]: {label: "En cours", color: "info", pulse: true},
    [PRINT_STATE.PRELOADED]: {label: "Préchargé", color: "warning"},
    [PRINT_STATE.READY]: {label: "Prête", color: "success"},
    [PRINT_STATE.EMPTY]: {label: "File vide", color: "grey"},
};

const SENDING_DISPLAY = {label: "Envoi en cours", color: "primary", pulse: true};

const STALE_DISPLAY = {label: "Non traitée", color: "warning"};

const pulse = keyframes`
    50% { opacity: 0.25; }
`;

const toneColor = (theme, color) => color === "grey" ? theme.palette.grey[500] : theme.palette[color].main;

const toneTextColor = (theme, color) => color === "grey" ? theme.palette.grey[800] : theme.palette[color].dark;

function DockTile({machine, onOpenTasks, onLocate, onError}) {
    const [printRequest, setPrintRequest] = useState(null)
    const [requestStale, setRequestStale] = useState(false)
    const [openResendDialog, setOpenResendDialog] = useState(false)

    const {state, canPrint} = getMachinePrintState(machine)
    // File vide : il ne reste rien qu'un message en attente pourrait imprimer, garder « Non traitée » tromperait l'opérateur.
    const requestSettled = printRequest !== null && (isRequestPickedUp(machine, printRequest) || state === PRINT_STATE.EMPTY)
    const isSending = printRequest !== null && !requestSettled && !requestStale
    const isStale = printRequest !== null && !requestSettled && requestStale

    useEffect(() => {
        if (requestSettled) {
            setPrintRequest(null)
            setRequestStale(false)
            setOpenResendDialog(false)
        }
    }, [requestSettled]);

    // Passé ce délai, le bouton se réactive mais exige une confirmation : le premier message peut encore être en file
    // (worker relancé par la tâche DSM, dossier chaud occupé) et un second appui imprimerait un fichier de plus.
    useEffect(() => {
        if (!printRequest) {
            return
        }

        const timer = setTimeout(() => setRequestStale(true), PICKUP_TIMEOUT_MS)
        return () => clearTimeout(timer)
    }, [printRequest]);

    const sendPrint = () => {
        const previousRequest = printRequest
        setPrintRequest({preloadedTaskIds: getPreloadedTaskIds(machine), sentAt: Date.now()})
        setRequestStale(false)

        printFirstTask(machine).then(result => {
            if (result.response !== "success") {
                setPrintRequest(previousRequest)
                setRequestStale(previousRequest !== null)
                onError(`Impression non envoyée sur la ${machine.number} : ${result.error || "le serveur a refusé la demande"}`)
            }
        })
    }

    const handlePrintClick = () => {
        if (isStale) {
            setOpenResendDialog(true)
            return
        }

        sendPrint()
    }

    const handleConfirmResend = () => {
        setOpenResendDialog(false)
        sendPrint()
    }

    const getDisplay = () => {
        if (state === PRINT_STATE.OFF) {
            return STATE_DISPLAY[PRINT_STATE.OFF]
        }

        if (isSending) {
            return SENDING_DISPLAY
        }

        if (isStale) {
            return STALE_DISPLAY
        }

        return STATE_DISPLAY[state]
    }

    // Quand tout va bien, l'icône suffit ; un mot n'apparaît que pour expliquer pourquoi le bouton se comporte autrement.
    const getPrintButton = () => {
        const number = machine.number

        if (isSending) {
            return {content: <CircularProgress size={20} color="inherit"/>, label: `Envoi en cours sur la ${number}`}
        }

        if (state === PRINT_STATE.OFF) {
            return {content: "Éteinte", label: `Machine ${number} éteinte`}
        }

        if (isStale && canPrint) {
            return {content: "Renvoyer", label: `Renvoyer l'impression sur la ${number}`, color: "warning"}
        }

        if (state === PRINT_STATE.PRINTING) {
            return {content: "En cours", label: `Impression en cours sur la ${number}`}
        }

        if (state === PRINT_STATE.PRELOADED && !canPrint) {
            return {content: "Préchargé", label: `Fichier préchargé sur la ${number}`}
        }

        if (state === PRINT_STATE.PRELOADED && machine.preload) {
            return {content: "Valider", label: `Valider le préchargé sur la ${number}`}
        }

        if (machine.preload) {
            return {content: <Downloading/>, label: `Précharger sur la ${number}`}
        }

        return {content: <Print/>, label: `Imprimer sur la ${number}`}
    }

    const display = getDisplay()
    const printButton = getPrintButton()
    const elapsedSeconds = printRequest ? Math.round((Date.now() - printRequest.sentAt) / 1000) : 0

    return (
        <Paper
            variant="outlined"
            sx={{
                flex: "0 0 188px",
                display: "grid",
                gridTemplateColumns: "56px minmax(0, 1fr)",
                overflow: "hidden",
                borderRadius: 2,
            }}
        >
            <ButtonBase
                onClick={() => onOpenTasks(machine)}
                aria-label={`Tâches en cours de la machine ${machine.number}`}
                sx={(theme) => ({
                    bgcolor: alpha(toneColor(theme, display.color), 0.12),
                    color: toneTextColor(theme, display.color),
                    borderRight: 1,
                    borderColor: "divider",
                    "&.Mui-focusVisible": {
                        outline: `3px solid ${theme.palette.primary.main}`,
                        outlineOffset: -3,
                    },
                })}
            >
                <Typography
                    component="span"
                    sx={{fontSize: 30, fontWeight: 700, lineHeight: 1, fontVariantNumeric: "tabular-nums"}}
                >
                    {machine.number}
                </Typography>
            </ButtonBase>

            <Box sx={{minWidth: 0, p: 0.75, pl: 1, display: "grid", gap: 0.75}}>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5}}>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
                        {machine.isReceiving
                            ? `Réception ${machine.processedTasks || 0}/${machine.incomingTasks || 0}`
                            : `${countWaitingTasks(machine)} en attente`}
                    </Typography>
                    <Box
                        component="span"
                        role="img"
                        aria-label={display.label}
                        sx={(theme) => ({
                            flexShrink: 0,
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: toneColor(theme, display.color),
                            animation: display.pulse ? `${pulse} 1.1s ease-in-out infinite` : "none",
                            "@media (prefers-reduced-motion: reduce)": {animation: "none"},
                        })}
                    />
                </Box>

                <Box sx={{display: "grid", gridTemplateColumns: "minmax(0, 1fr) 44px", gap: 0.5}}>
                    <Button
                        variant="contained"
                        color={printButton.color || "primary"}
                        disableElevation
                        disabled={!canPrint || isSending}
                        onClick={handlePrintClick}
                        aria-label={printButton.label}
                        sx={{
                            height: 48,
                            minWidth: 0,
                            px: 0.5,
                            fontSize: 13,
                            fontWeight: 600,
                            textTransform: "none",
                            "&.Mui-disabled": {bgcolor: "action.hover", color: "text.secondary"},
                        }}
                    >
                        {printButton.content}
                    </Button>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => onLocate(machine)}
                        aria-label={`Voir la carte de la machine ${machine.number}`}
                        sx={{
                            height: 48,
                            minWidth: 0,
                            px: 0,
                            flexDirection: "column",
                            gap: 0,
                            fontSize: 10.5,
                            lineHeight: 1.1,
                            textTransform: "none",
                            borderColor: "divider",
                        }}
                    >
                        <ArrowDownward fontSize="small"/>
                        Voir
                    </Button>
                </Box>
            </Box>

            <Dialog open={openResendDialog && isStale} onClose={() => setOpenResendDialog(false)}>
                <DialogTitle>Impression toujours en attente sur la {machine.number}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        La demande envoyée il y a {elapsedSeconds} s n'a pas encore été prise en charge par le poste
                        d'impression. Si vous renvoyez, un fichier de plus sera imprimé dès qu'il reprendra.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2, gap: 1}}>
                    <Button size="large" onClick={() => setOpenResendDialog(false)} autoFocus sx={{minHeight: 48}}>
                        Attendre
                    </Button>
                    <Button size="large" variant="contained" color="warning" onClick={handleConfirmResend} sx={{minHeight: 48}}>
                        Imprimer un fichier de plus
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}

export default memo(DockTile)
